// Quote-card composer for Claude the Explorer expedition reports
// (SPACES_NEXT_LAYER_SPEC Arc 7, quote cards v1 — report page only).
//
// Composes a 1200x630 postcard PNG on an offscreen canvas: scene snapshot
// (when present) on top, the bot's quote wrapped below, bot name + grade
// chip, and the brand watermark (crab glyph + "myspaice.ai" with the green
// i's — ruled 08-18; replaced the stale "spaceexplorer.ai" text, which
// predated the 08-14 canonical-domain ruling). Visual language mirrors
// ExplorerReportPage's report card: parchment gradient, deep-purple border,
// gold accents, on the page's dark-nebula mat.
//
// CORS reality (tested 2026-07-19): the snapshot bucket
// spaceexplorer-content-files.s3.us-east-1.amazonaws.com serves
// `Access-Control-Allow-Origin: *` (+ `Vary: Origin`) on GET and preflight.
// We still load via fetch(mode:'cors') -> blob -> ImageBitmap instead of
// img.crossOrigin, because the page's plain <img> tags may have already
// cached a no-CORS response for the same URL; a blob decoded locally is
// same-origin and can never taint the canvas. If the image fails to load
// for ANY reason, we degrade to a typography-only card — never a broken
// share.

const CARD_W = 1200;
const CARD_H = 630;

// Palette lifted from ExplorerReportPage's inline styles.
const C = {
  matTop: '#2a1c4d',
  matMid: '#17101f',
  matBottom: '#0d0a12',
  parchTop: '#fffdf5',
  parchBottom: '#f4ecd7',
  ink: '#3a2f4d',
  inkSoft: '#8a7ba3',
  purple: '#5b3a8e',
  gold: '#ffd76e',
  matText: 'rgba(237, 231, 255, 0.8)',
};

const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

// ── Brand watermark ──────────────────────────────────────────────────────
// Same glyph geometry as the index.html shell / ExploreSpacesPage deck
// bridge / tools/assets/watermark masters — redraw them together or not at
// all. Ink baked to the mat text color; green matches the white-ink lockup
// accent. A data: URI image is same-origin by definition, so drawing it can
// never taint the canvas (the same paranoia as the snapshot loader above).
const WM_GREEN = '#7ee287';
const WM_GLYPH_URI = 'data:image/svg+xml;utf8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><defs><mask id="k"><rect width="64" height="64" fill="white"/><circle cx="26.6" cy="29.3" r="2.4" fill="black"/><circle cx="37.4" cy="29.3" r="2.4" fill="black"/><path d="M11 21 L7.5 12.5 L14.5 12.5 Z" fill="black"/><path d="M53 21 L49.5 12.5 L56.5 12.5 Z" fill="black"/></mask></defs><g mask="url(#k)"><circle cx="32" cy="30" r="16" fill="none" stroke="#EDE7FF" stroke-width="3"/><circle cx="32" cy="33" r="10" fill="#EDE7FF"/><path d="M17 38 C10 36 7.5 29 9.5 24.5" fill="none" stroke="#EDE7FF" stroke-width="3" stroke-linecap="round"/><path d="M47 38 C54 36 56.5 29 54.5 24.5" fill="none" stroke="#EDE7FF" stroke-width="3" stroke-linecap="round"/><circle cx="11" cy="19.5" r="5.5" fill="#EDE7FF"/><circle cx="53" cy="19.5" r="5.5" fill="#EDE7FF"/></g></svg>'
);
// Resolves to an HTMLImageElement or null — the card must never fail (or
// hang) on its own branding, so failure and slow-decode both fall through
// to the text-only mark.
const loadWmGlyph = () => new Promise((resolve) => {
  try {
    const img = new Image();
    const timer = setTimeout(() => resolve(null), 400);
    img.onload = () => { clearTimeout(timer); resolve(img); };
    img.onerror = () => { clearTimeout(timer); resolve(null); };
    img.src = WM_GLYPH_URI;
  } catch (e) { resolve(null); }
});

// ── Snapshot loader ──────────────────────────────────────────────────────
// Resolves to an ImageBitmap/HTMLImageElement, or null on any failure.
const loadSnapshot = async (url) => {
  try {
    const res = await fetch(url, { mode: 'cors', cache: 'no-store' });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (typeof createImageBitmap === 'function') {
      try { return await createImageBitmap(blob); } catch (e) { /* fall through */ }
    }
    // Older Safari: decode via an object URL (blob: is same-origin, no taint).
    return await new Promise((resolve) => {
      const obj = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(obj); resolve(img); };
      img.onerror = () => { URL.revokeObjectURL(obj); resolve(null); };
      img.src = obj;
    });
  } catch (e) {
    return null;
  }
};

// ── Canvas helpers ───────────────────────────────────────────────────────
const roundRectPath = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, w, h, r);
    return;
  }
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

// Greedy word-wrap at a given font size; returns lines (or null if it
// can't fit within maxLines).
const wrapAt = (ctx, text, size, weight, maxWidth, maxLines) => {
  ctx.font = `${weight} ${size}px ${FONT}`;
  const words = String(text).split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  for (const word of words) {
    const probe = line ? `${line} ${word}` : word;
    if (ctx.measureText(probe).width <= maxWidth || !line) {
      line = probe;
    } else {
      lines.push(line);
      line = word;
      if (lines.length >= maxLines) return null;
    }
  }
  if (line) lines.push(line);
  return lines.length <= maxLines ? lines : null;
};

// Shrink-to-fit: walk font sizes down until the quote fits BOTH the line
// budget and the vertical area (07-20 fix: the first ship fit width/lines
// only, so long quotes overflowed the footer); hard-ellipsize at the floor
// size as a last resort. Line height = size * 1.3, matching the renderer.
const fitQuote = (ctx, text, maxWidth, maxLines, maxSize, minSize, areaH) => {
  const heightCap = (size) => Math.min(maxLines, Math.max(1, Math.floor(areaH / (size * 1.3))));
  for (let size = maxSize; size >= minSize; size -= 2) {
    const lines = wrapAt(ctx, text, size, 700, maxWidth, heightCap(size));
    if (lines) return { lines, size };
  }
  const size = minSize;
  const lineCap = heightCap(size);
  ctx.font = `700 ${size}px ${FONT}`;
  const words = String(text).split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  for (const word of words) {
    const probe = line ? `${line} ${word}` : word;
    if (ctx.measureText(probe).width <= maxWidth || !line) {
      line = probe;
    } else {
      lines.push(line);
      line = word;
      if (lines.length === lineCap) break;
    }
  }
  if (lines.length < lineCap && line) lines.push(line);
  let last = lines[lines.length - 1] || '';
  while (last && ctx.measureText(`${last}…`).width > maxWidth) {
    last = last.replace(/\s*\S+$/, '');
  }
  lines[lines.length - 1] = `${last}…`;
  return { lines, size };
};

// ── Composer ─────────────────────────────────────────────────────────────
// opts: { quote, botName, grade, kindLabel, dateLabel, imageUrl }
// Resolves { blob, dataUrl, withImage }. Never rejects for image reasons —
// a failed snapshot just means a typography-only card.
export const composeQuoteCard = async (opts) => {
  const {
    quote = '',
    botName = 'Claude the Explorer',
    grade = null,
    kindLabel = '',
    dateLabel = '',
    imageUrl = null,
  } = opts || {};

  const snapshot = imageUrl ? await loadSnapshot(imageUrl) : null;
  const withImage = !!snapshot;

  const canvas = document.createElement('canvas');
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext('2d');

  // Dark nebula mat (the page background), postcard floating on it.
  const mat = ctx.createRadialGradient(CARD_W / 2, -80, 60, CARD_W / 2, CARD_H, CARD_W);
  mat.addColorStop(0, C.matTop);
  mat.addColorStop(0.5, C.matMid);
  mat.addColorStop(1, C.matBottom);
  ctx.fillStyle = mat;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // Postcard frame — parchment with the report card's purple border.
  const px = 26;
  const py = 26;
  const pw = CARD_W - px * 2;
  const ph = CARD_H - py - 62; // taller bottom mat strip for the watermark
  const radius = 22;

  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.55)';
  ctx.shadowBlur = 40;
  ctx.shadowOffsetY = 10;
  const parch = ctx.createLinearGradient(0, py, 0, py + ph);
  parch.addColorStop(0, C.parchTop);
  parch.addColorStop(1, C.parchBottom);
  roundRectPath(ctx, px, py, pw, ph, radius);
  ctx.fillStyle = parch;
  ctx.fill();
  ctx.restore();
  roundRectPath(ctx, px, py, pw, ph, radius);
  ctx.strokeStyle = C.purple;
  ctx.lineWidth = 6;
  ctx.stroke();

  // Snapshot band (cover-crop). 07-20 fix: the crop must be clipped to the
  // BAND, not the whole postcard — 1280x720 snapshots cover-scale by width,
  // draw ~640px tall, and were flooding over the quote and footer.
  const bandH = withImage ? 280 : 0;
  if (withImage) {
    ctx.save();
    roundRectPath(ctx, px + 3, py + 3, pw - 6, ph - 6, radius - 4);
    ctx.clip();
    ctx.beginPath();
    ctx.rect(px + 3, py + 3, pw - 6, bandH);
    ctx.clip();
    const iw = snapshot.width || 1280;
    const ih = snapshot.height || 720;
    const scale = Math.max((pw - 6) / iw, bandH / ih);
    const dw = iw * scale;
    const dh = ih * scale;
    const dx = px + 3 + ((pw - 6) - dw) / 2;
    const dy = py + 3 + (bandH - dh) / 2;
    ctx.drawImage(snapshot, dx, dy, dw, dh);
    ctx.restore();
    // Divider under the image, matching the note-slip border tone (drawn
    // outside the band clip, which would otherwise swallow it).
    ctx.fillStyle = 'rgba(91, 58, 142, 0.45)';
    ctx.fillRect(px + 3, py + 3 + bandH, pw - 6, 2);
  }

  // Quote block — bold ink on parchment, curly-quoted like the report card.
  const textWidth = pw - 128;
  const quoteTop = py + (withImage ? bandH + 26 : 60);
  const footerH = 92;
  const quoteAreaH = (py + ph - footerH) - quoteTop;
  const maxLines = withImage ? 3 : 6;
  const maxSize = withImage ? 42 : 52;
  const displayQuote = `“${String(quote).trim()}”`;
  const { lines, size } = fitQuote(ctx, displayQuote, textWidth, maxLines, maxSize, 26, quoteAreaH);
  const lineH = Math.round(size * 1.3);
  const blockH = lines.length * lineH;
  let ty = quoteTop + Math.max(0, (quoteAreaH - blockH) / 2) + lineH * 0.8;
  ctx.fillStyle = C.ink;
  ctx.font = `700 ${size}px ${FONT}`;
  ctx.textAlign = 'center';
  for (const line of lines) {
    ctx.fillText(line, px + pw / 2, ty);
    ty += lineH;
  }

  // Footer row: attribution left, grade chip right.
  const footY = py + ph - 40;
  ctx.textAlign = 'left';
  ctx.fillStyle = C.inkSoft;
  ctx.font = `800 24px ${FONT}`;
  const attribution = [`🦀 ${botName}`, kindLabel, dateLabel].filter(Boolean).join('  ·  ');
  ctx.fillText(attribution, px + 40, footY + 8);

  if (grade) {
    const label = `GRADE ${String(grade).toUpperCase()}`;
    ctx.font = `900 26px ${FONT}`;
    const tw = ctx.measureText(label).width;
    const chipW = tw + 44;
    const chipH = 48;
    const chipX = px + pw - 40 - chipW;
    const chipY = footY - chipH / 2 - 4;
    roundRectPath(ctx, chipX, chipY, chipW, chipH, chipH / 2);
    ctx.fillStyle = C.purple;
    ctx.fill();
    ctx.strokeStyle = C.gold;
    ctx.lineWidth = 2.5;
    ctx.stroke();
    ctx.fillStyle = C.gold;
    ctx.fillText(label, chipX + 22, chipY + 33);
  }

  // Brand watermark on the dark mat strip, bottom-right: crab glyph +
  // myspaice.ai with the green i's. Segments draw left-to-right from a
  // measured start so the mark stays right-anchored at 30px; the glyph is
  // optional — if its decode failed the text mark still renders alone.
  const wmGlyph = await loadWmGlyph();
  const wmSize = 22;
  const wmY = CARD_H - 20;
  const wmSeg = [
    ['myspa', C.matText, `400 ${wmSize}px ${FONT}`],
    ['i', WM_GREEN, `700 ${wmSize}px ${FONT}`],
    ['ce.a', C.matText, `400 ${wmSize}px ${FONT}`],
    ['i', WM_GREEN, `700 ${wmSize}px ${FONT}`],
  ];
  ctx.textAlign = 'left';
  let wmTextW = 0;
  for (const [t, , f] of wmSeg) { ctx.font = f; wmTextW += ctx.measureText(t).width; }
  let wmX = CARD_W - 30 - wmTextW;
  if (wmGlyph) {
    const gh = 26;
    ctx.save();
    ctx.globalAlpha = 0.85;
    ctx.drawImage(wmGlyph, wmX - gh - 9, wmY - gh + 4, gh, gh);
    ctx.restore();
  }
  for (const [t, color, f] of wmSeg) {
    ctx.font = f;
    ctx.fillStyle = color;
    ctx.fillText(t, wmX, wmY);
    wmX += ctx.measureText(t).width;
  }

  // Export. A tainted canvas throws SecurityError here; our snapshot path
  // is blob-decoded (same-origin) so this should never fire, but if it
  // does, recompose without the image rather than failing the share.
  try {
    const dataUrl = canvas.toDataURL('image/png');
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob null'))), 'image/png');
    });
    return { blob, dataUrl, withImage };
  } catch (e) {
    if (withImage) return composeQuoteCard({ ...opts, imageUrl: null });
    throw e;
  }
};

// X/Twitter intent URL for a quote card (ShareButton.js intent pattern).
export const quoteCardIntentUrl = (quote, url) => {
  const trimmed = String(quote || '').trim();
  const short = trimmed.length > 200 ? `${trimmed.slice(0, 199).trim()}…` : trimmed;
  const text = `"${short}" · Claude the Explorer 🦀`;
  return `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
};

// True when this device should get the native share sheet with the PNG
// attached (theater's touch-share heuristic + canShare files support).
export const canNativeShareFile = (file) => {
  const touch = navigator.maxTouchPoints > 0
    || (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
  return touch
    && typeof navigator.share === 'function'
    && typeof navigator.canShare === 'function'
    && navigator.canShare({ files: [file] });
};
